import { Flex } from '@chakra-ui/react';
import { Modal, Button, useToast } from '@morressier/alembic';
import { useEditorialUploadICMJEForm } from 'src/api/commands';
import { IDecisionModalPropsForSubmittingAuthor } from '../decisionModals/decisionModalHelper.js';
import { FormProvider, useForm } from 'react-hook-form';
import { useWorkflow } from 'src/hooks/useWorkflow';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { CustomFormFieldDefinition } from '@publishing-engine/domain/types/article';
import { CustomFormFieldComponent } from 'src/components/FormField';
import { GenericFormFields } from '@publishing-engine/submission-service/src/workflow-configuration/generic-form-fields/generic-form-fields';
import { generateValidationSchema } from 'src/utils/validation';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { queryClient } from '../../../utils/queryClient.js';

export const EditorialUploadICMJEFormModal = ({
  onClose,
  submissionId,
  submissionContextId,
  authorEmail
}: IDecisionModalPropsForSubmittingAuthor) => {
  const {
    forms: { editorialUploadICMJE },
    articleWorkflow
  } = useWorkflow();

  if (!editorialUploadICMJE) {
    return <></>;
  }

  const toast = useToast();

  const { refetchSubmission, organizationId } = useSubmissionData();
  const editorialUploadICMJEMutation = useEditorialUploadICMJEForm({
    submissionId,
    organizationId,
    submissionContextId,
    articleWorkflowId: articleWorkflow.id,
    invalidateSubmissionQueries: refetchSubmission
  });

  const uploadICMJEFields = new GenericFormFields(editorialUploadICMJE);

  const config = {
    ICMJEFormFile: uploadICMJEFields.getFieldConfig(
      'ICMJEFormFile'
    ) as CustomFormFieldDefinition
  };
  const schema = generateValidationSchema(config);

  const methods = useForm({
    defaultValues: {
      icmjeForm: {
        file: {}
      }
    },
    resolver: ajvResolver(schema)
  });
  const { handleSubmit, getValues } = methods;

  const submit = async () => {
    const formValues = getValues();
    const payload = {
      ...formValues,
      icmjeForm: {
        ...formValues.icmjeForm,
        email: authorEmail,
        isSubmittingAuthor: true
      }
    };

    const res = await editorialUploadICMJEMutation.mutateAsync(payload);

    if (res.isSuccess) {
      toast({
        variant: 'success',
        title: 'Forms and Disclosures Uploaded'
      });
      queryClient.invalidateQueries([
        'submitting-author-only-icmje-form-editorial',
        { submissionId, roleType: 'editorial' }
      ]);
      onClose();
    } else {
      toast({
        variant: 'error',
        title: 'Error uploading Forms and Disclosures'
      });
    }
  };

  return (
    <Modal isOpen onClose={onClose} width="720px">
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(submit)}>
          <Flex padding={8} gap={6} flexDirection="column" overflow="auto">
            {config.ICMJEFormFile && (
              <CustomFormFieldComponent config={config.ICMJEFormFile} />
            )}

            <Flex gap={2} justifyContent="flex-end">
              <Button onClick={onClose} variant="secondary">
                Cancel
              </Button>
              <Button
                type="submit"
                isLoading={editorialUploadICMJEMutation.isPending}
              >
                Upload
              </Button>
            </Flex>
          </Flex>
        </form>
      </FormProvider>
    </Modal>
  );
};
